import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { EntityTypeEnum } from 'src/shared/enums';

import { FeatureEnum } from '../../enums/feature.enum';
import { IFeaturePermissions } from '../../interfaces/feature-permissions.interface';
import { ISchoolSettings } from '../../interfaces/groups/school-settings.interface';
import { IHomeworkSchoolSetting } from '../../interfaces/homework-school.interface';
import { IMealSetting } from '../../interfaces/meal.interface';
import { INurserySettingDTOIn } from '../../interfaces/nursery.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { AuthState, HomeworkSchoolState, MealsState, NurseryState } from '../../store/state';

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    constructor(private store: Store) {}

    public checkPermission(userAccessLevel: number, currentUserAccessLevel: number, feature: IFeaturePermissions): boolean {
        if (feature.mustBeImpersonated) {
            if (this.store.selectSnapshot(AuthState.currentUserType) !== EntityTypeEnum.STUDENT) {
                return false;
            }
        }
        if (feature.byPassImpersonation) {
            if (feature.feature === FeatureEnum.NURSERY_ENCODE) {
                const schoolSettings: ISchoolSettings = this.store.selectSnapshot(AuthState.currentSchoolSettings);
                if (!schoolSettings.shop_setting.nursery) {
                    return false;
                }
                const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                if ([6, 7].includes(connectedUser.accessLevel)) {
                    const nurserySettings: INurserySettingDTOIn[] = this.store.selectSnapshot(NurseryState.getNurserySettings);
                    let personInChargeToCheck: number[] = [];
                    nurserySettings.map((nurserySetting: INurserySettingDTOIn) => {
                        personInChargeToCheck = [...personInChargeToCheck, ...nurserySetting.employee_ids];
                    });
                    return personInChargeToCheck.includes(connectedUser.id);
                }
            }
            return feature.accessLevelRequired.includes(userAccessLevel);
        } else {
            // if the feature require another type of check, we can do it here (like supervisor for settings in meals or else)
            const schoolSettings: ISchoolSettings = this.store.selectSnapshot(AuthState.currentSchoolSettings);

            // custom shop access for teachers and supervisors in charge
            if (feature.feature === FeatureEnum.SHOPPING_NAV) {
                // if shop is disabled in school settings
                if (!schoolSettings?.shop_setting?.is_active) {
                    return false;
                }
                const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                if ([6, 7].includes(connectedUser.accessLevel)) {
                    // TEACHER || SUPERVISOR
                    const mealsSetting: IMealSetting[] = this.store.selectSnapshot(MealsState.schoolMealsSettings);
                    const nurserySettings: INurserySettingDTOIn[] = this.store.selectSnapshot(NurseryState.getNurserySettings);
                    const homeworkSchoolSettings: IHomeworkSchoolSetting = this.store.selectSnapshot(
                        HomeworkSchoolState.schoolHomeworkSchoolSettings
                    );

                    let personInChargeToCheck: number[] = [];

                    mealsSetting.map((mealSetting: IMealSetting) => {
                        personInChargeToCheck = [...personInChargeToCheck, ...mealSetting.employee_ids];
                    });

                    nurserySettings.map((nurserySetting: INurserySettingDTOIn) => {
                        personInChargeToCheck = [...personInChargeToCheck, ...nurserySetting.employee_ids];
                    });

                    personInChargeToCheck = [...personInChargeToCheck, ...homeworkSchoolSettings.employee_ids];
                    return personInChargeToCheck.includes(connectedUser.id);
                }
            }
            // EMPLOYEE AS STUDENT for WALLET, HOMEWORK, MEAL
            if (feature.feature === FeatureEnum.WALLET_ADMIN || feature.feature === FeatureEnum.MEALS_TABS) {
                if (
                    this.store.selectSnapshot(AuthState.user).type === EntityTypeEnum.EMPLOYEE &&
                    this.store.selectSnapshot(AuthState.currentUserType) === EntityTypeEnum.STUDENT
                ) {
                    return false;
                }
            }
            if (feature.feature === FeatureEnum.WALLET_STUDENT) {
                const user: IUser = this.store.selectSnapshot(AuthState.user);
                if (
                    user.type === EntityTypeEnum.EMPLOYEE &&
                    [2, 3, 4, 5].includes(user.accessLevel) &&
                    this.store.selectSnapshot(AuthState.currentUserType) === EntityTypeEnum.STUDENT
                ) {
                    return true;
                }
            }
            // MEALS
            if (
                feature.feature === FeatureEnum.MEALS_NAV ||
                feature.feature === FeatureEnum.MEALS_SETTINGS ||
                feature.feature === FeatureEnum.MEALS_BOOKING_EMPLOYEES ||
                feature.feature === FeatureEnum.MEALS_HISTORY
            ) {
                if (!schoolSettings.shop_setting.meals) {
                    return false;
                }
                const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                if ([6, 7].includes(connectedUser.accessLevel)) {
                    const mealsSetting: IMealSetting[] = this.store.selectSnapshot(MealsState.schoolMealsSettings);
                    let personInChargeToCheck: number[] = [];
                    mealsSetting.map((mealSetting: IMealSetting) => {
                        personInChargeToCheck = [...personInChargeToCheck, ...mealSetting.employee_ids];
                    });
                    return personInChargeToCheck.includes(connectedUser.id);
                }
            }
            // NURSERY
            if (feature.feature === FeatureEnum.NURSERY_NAV || feature.feature === FeatureEnum.NURSERY_SETTINGS) {
                if (!schoolSettings.shop_setting.nursery) {
                    return false;
                }
                const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                if ([6, 7].includes(connectedUser.accessLevel)) {
                    const nurserySettings: INurserySettingDTOIn[] = this.store.selectSnapshot(NurseryState.getNurserySettings);
                    let personInChargeToCheck: number[] = [];
                    nurserySettings.map((nurserySetting: INurserySettingDTOIn) => {
                        personInChargeToCheck = [...personInChargeToCheck, ...nurserySetting.employee_ids];
                    });
                    return personInChargeToCheck.includes(connectedUser.id);
                }
            }
            // HOMEWORK SCHOOL
            if (
                feature.feature === FeatureEnum.HOMEWORK_SCHOOL_NAV ||
                feature.feature === FeatureEnum.HOMEWORK_SCHOOL_SETTINGS ||
                feature.feature === FeatureEnum.HOMEWORK_SCHOOL_BOOKING_EMPLOYEES ||
                feature.feature === FeatureEnum.HOMEWORK_SCHOOL_HISTORY
            ) {
                if (!schoolSettings.shop_setting.homeworks) {
                    return false;
                }
                const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                if ([6, 7].includes(connectedUser.accessLevel)) {
                    const homeworkSchoolSettings: IHomeworkSchoolSetting = this.store.selectSnapshot(
                        HomeworkSchoolState.schoolHomeworkSchoolSettings
                    );
                    let personInChargeToCheck: number[] = [];

                    personInChargeToCheck = [...personInChargeToCheck, ...homeworkSchoolSettings.employee_ids];
                    return personInChargeToCheck.includes(connectedUser.id);
                }
            }

            // INCOMING FEATURE
            if (feature.feature === FeatureEnum.ACTIVITIES_NAV) {
                if (!schoolSettings.shop_setting.activities) {
                    return false;
                }
            }

            if (feature.feature === FeatureEnum.CLOUD_NAV) {
                return this.store.selectSnapshot(AuthState.currentSchoolHasMediaSettingsActivated);
            }
            console.log(currentUserAccessLevel);

            return feature.accessLevelRequired.includes(currentUserAccessLevel);
        }
    }

    public hasPermissions(feature: IFeaturePermissions): boolean {
        return this.checkPermission(
            this.store.selectSnapshot(AuthState.user).accessLevel,
            this.store.selectSnapshot(AuthState.currentUser).accessLevel,
            feature
        );
    }
}
